import type { Note } from '@/types/note'
import type { Task } from '@/services/taskService'
import type { Project } from '@/services/projectService'

/**
 * Get the route for viewing a note based on its context.
 */
export function getNoteRoute(note: Note): string {
  // Check explicit context first
  if (note.context === 'personal_project') {
    return `/my/notes/${note.uuid}`
  }
  
  if (note.context === 'personal') {
    return `/my/notes/${note.uuid}`
  }
  
  // FlashReports use their own route
  if (note.context === 'flash_report') {
    return `/sales/flash-reports/${note.uuid}`
  }
  
  // Fallback: infer from notable_type
  if (note.notable_type === 'App\\Models\\User' && note.notable_uuid) {
    // Check if this is the current user's note (would need authStore, but for now assume personal)
    return `/my/notes/${note.uuid}`
  }
  
  // Default to CRM route
  return `/crm/notes/${note.uuid}`
}

/**
 * Get the route for viewing a task based on its context.
 */
export function getTaskRoute(task: Task): string {
  // Check explicit context first
  if (task.context === 'personal') {
    return `/my/tasks/${task.uuid}`
  }
  
  // If context is null/undefined, default to CRM route (shouldn't happen with proper context)
  // But we'll allow it as a fallback for backward compatibility
  if (!task.context) {
    // Could infer from project if available, but for now default to CRM
    // This should be rare since context should always be set
    return `/crm/tasks/${task.uuid}`
  }
  
  // Default to CRM route for 'crm' context or any other context
  return `/crm/tasks/${task.uuid}`
}

/**
 * Get the route name for a note based on its context.
 */
export function getNoteRouteName(note: Note): string {
  if (note.context === 'personal_project' || note.context === 'personal') {
    return 'MyNoteDetails'
  }
  if (note.context === 'flash_report') {
    return 'FlashReportDetails'
  }
  return 'CRMNoteDetails'
}

/**
 * Get the route name for a task based on its context.
 */
export function getTaskRouteName(task: Task): string {
  if (task.context === 'personal') {
    return 'MyTaskDetails'
  }
  return 'CRMTaskDetails'
}

/**
 * Get the route for viewing a project based on its context.
 */
export function getProjectRoute(project: Project | { uuid: string; context?: string; projectable_type?: string }): string {
  // Check explicit context first
  if (project.context === 'personal') {
    return `/my/projects/${project.uuid}`
  }
  
  // Default to CRM route (including Contact projects)
  return `/crm/projects/${project.uuid}`
}

/**
 * Get the route name for a project based on its context.
 */
export function getProjectRouteName(project: Project | { uuid: string; context?: string; projectable_type?: string }): string {
  if (project.context === 'personal') {
    return 'MyProjectDetails'
  }
  // All other projects (including Contact projects) use CRM route
  return 'ProjectDetails'
}

